import { useState, useCallback } from "react";
import { incentiveRequestService } from "@/services/incentiveRequestService";

interface IApiProblemDetail {
  title?: string;
  detail?: string;
  status?: number;
}

function isApiError(err: unknown): err is { response: { status: number; data?: IApiProblemDetail } } {
  return (
    typeof err === "object" &&
    err !== null &&
    "response" in err &&
    typeof (err as Record<string, unknown>).response === "object" &&
    (err as Record<string, unknown>).response !== null &&
    "status" in ((err as Record<string, Record<string, unknown>>).response)
  );
}

function resolveErrorMessage(err: unknown): string {
  if (isApiError(err)) {
    const { status, data } = err.response;
    if (status === 404) {
      return data?.detail ?? "The file associated with this request no longer exists in storage.";
    }
    if (status === 401) {
      return "You are not authorised to download this file.";
    }
    if (data?.detail) {
      return data.detail;
    }
  }
  if (err instanceof Error) {
    return err.message;
  }
  return "Download failed. Please try again.";
}

interface IUseFileDownloadResult {
  /** ID of the request currently being downloaded (null when idle). */
  downloadingId: string | null;
  /** Error message from the last failed download attempt. */
  error: string | null;
  /** Initiates a direct file download for the given request ID. */
  download: (requestId: string) => Promise<void>;
  /** Clears the current error message. */
  clearError: () => void;
}

/**
 * Hook that downloads a file directly from the API as a blob
 * and triggers a browser download via a temporary object URL.
 */
export function useFileDownload(): IUseFileDownloadResult {
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const clearError = useCallback((): void => {
    setError(null);
  }, []);

  const download = useCallback(async (requestId: string): Promise<void> => {
    setDownloadingId(requestId);
    setError(null);

    try {
      if (!requestId || requestId.trim().length === 0) {
        throw new Error("Invalid request ID");
      }

      const { blob, fileName } = await incentiveRequestService.downloadFile(requestId);

      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    } catch (err) {
      setError(resolveErrorMessage(err));
    } finally {
      setDownloadingId(null);
    }
  }, []);

  return { downloadingId, error, download, clearError };
}
